/**
 * OpenOrders - Displays user's open (unfilled) orders with cancel functionality.
 */

import { memo, useCallback } from 'react'
import { X } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { Order } from '../../engine/types'

export interface OpenOrdersProps {
  /** List of user's open orders */
  orders: Order[]
  /** Callback when cancel button is clicked */
  onCancelOrder: (orderId: string) => void
  /** Whether cancel functionality is enabled */
  enableCancel?: boolean
  /** Optional className */
  className?: string
}

/**
 * Single order row component.
 */
const OrderRow = memo(function OrderRow({
  order,
  onCancel,
  enableCancel,
}: {
  order: Order
  onCancel: () => void
  enableCancel: boolean
}) {
  const isBuy = order.side === 'buy'
  const priceDisplay = Math.round(order.price * 100)
  const remaining = order.quantity - order.filledQuantity

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md border px-3 py-2',
        isBuy ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'
      )}
    >
      {/* Side indicator */}
      <span
        className={cn(
          'rounded px-2 py-0.5 text-xs font-medium',
          isBuy ? 'bg-green-500/20 text-green-700' : 'bg-red-500/20 text-red-700'
        )}
      >
        {isBuy ? 'BUY' : 'SELL'}
      </span>

      {/* Order details */}
      <div className="flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-sm tabular-nums text-foreground">
            {remaining} shares
          </span>
          <span className="text-xs text-muted-foreground">@</span>
          <span className="font-mono text-sm tabular-nums text-foreground">
            {priceDisplay}¢
          </span>
        </div>

        {/* Partial fill indicator */}
        {order.filledQuantity > 0 && (
          <p className="text-xs text-muted-foreground">
            {order.filledQuantity} of {order.quantity} filled
          </p>
        )}
      </div>

      {/* Cancel button */}
      {enableCancel && (
        <button
          type="button"
          onClick={onCancel}
          className={cn(
            'rounded-md p-1.5 transition-colors',
            'text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
            'focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1'
          )}
          aria-label={`Cancel ${isBuy ? 'buy' : 'sell'} order for ${remaining} shares at ${priceDisplay}¢`}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
})

/**
 * OpenOrders displays a list of user's open orders.
 */
export const OpenOrders = memo(function OpenOrders({
  orders,
  onCancelOrder,
  enableCancel = true,
  className,
}: OpenOrdersProps) {
  const handleCancel = useCallback(
    (orderId: string) => {
      onCancelOrder(orderId)
    },
    [onCancelOrder]
  )

  // Filter to only show open/partial orders
  const openOrders = orders.filter(
    (o) => o.status === 'open' || o.status === 'partial'
  )

  return (
    <div
      className={cn('rounded-lg border border-border bg-card p-4', className)}
      role="region"
      aria-label="Open orders"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">Open Orders</h3>
        <span className="text-xs text-muted-foreground">
          {openOrders.length} {openOrders.length === 1 ? 'order' : 'orders'}
        </span>
      </div>

      {openOrders.length === 0 ? (
        <div className="py-6 text-center">
          <p className="text-sm text-muted-foreground">
            No open orders
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Your pending orders will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {openOrders.map((order) => (
            <OrderRow
              key={order.id}
              order={order}
              onCancel={() => handleCancel(order.id)}
              enableCancel={enableCancel}
            />
          ))}
        </div>
      )}

      {/* Beginner hint */}
      {openOrders.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          These orders are waiting to be matched. Click the X to cancel.
        </p>
      )}
    </div>
  )
})
